import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { api, resolveImageUrl } from '@/lib/axios';
import { toast } from 'sonner';
import {
  Search,
  Plus,
  MoreHorizontal,
  Eye,
  Edit2,
  Trash2,
  Printer,
  MessageCircle,
  Filter,
  SlidersHorizontal,
  ChevronLeft,
  ChevronRight,
  Image as ImageIcon,
} from 'lucide-react';
import { formatWhatsAppNumber } from '@/lib/constants';

interface OrderItemData {
  id?: number;
  productId?: number;
  productName: string;
  quantity: number;
  unitPrice: number;
  filamentColor?: string;
  variantNotes?: string;
  product?: {
    id: number;
    name: string;
    mainImageUrl?: string;
  };
}

interface OrderData {
  id: number;
  guestName?: string;
  guestWhatsApp?: string;
  guestEmail?: string;
  guestAddress?: string;
  customer?: { fullName: string; phoneNumber?: string; email?: string };
  deliveryMethod?: string;
  status: string;
  paymentStatus: string;
  paymentMethod?: string;
  subtotal?: number;
  discount?: number;
  total: number;
  notes?: string;
  source?: string;
  createdAt: string;
  items?: OrderItemData[];
}

const TABS = [
  { label: 'Todos', value: 'all' },
  { label: 'Pendientes', value: 'pending' },
  { label: 'Enviados', value: 'shipped' },
  { label: 'Entregados', value: 'delivered' },
  { label: 'Cancelados', value: 'cancelled' },
];

export default function OrdersPage() {
  const [orders, setOrders] = useState<OrderData[]>([]);
  const [selectedTab, setSelectedTab] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [isLoading, setIsLoading] = useState(true);

  // Pagination
  const [pageSize, setPageSize] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);

  // Selection
  const [selectedOrders, setSelectedOrders] = useState<number[]>([]);

  // Modal para Crear / Editar Pedido
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingOrder, setEditingOrder] = useState<Partial<OrderData> | null>(null);
  const [orderItemsDraft, setOrderItemsDraft] = useState<OrderItemData[]>([
    { productName: 'Lámpara Ripple', quantity: 1, unitPrice: 24500, filamentColor: 'Blanco Hueso' },
  ]);

  // Modal para Borrado
  const [deletingOrderId, setDeletingOrderId] = useState<number | null>(null);

  useEffect(() => {
    loadOrders();
  }, [selectedTab]);

  const loadOrders = async () => {
    try {
      setIsLoading(true);
      const queryParams = new URLSearchParams();
      if (selectedTab !== 'all') {
        queryParams.set('status', selectedTab);
      }
      queryParams.set('pageSize', '100');
      const res = await api.get<{ items: OrderData[] }>(`/orders?${queryParams.toString()}`);
      setOrders(Array.isArray(res.data?.items) ? res.data.items : []);
    } catch {
      toast.error('Error al cargar la lista de pedidos');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      setSelectedOrders(filteredOrders.map((o) => o.id));
    } else {
      setSelectedOrders([]);
    }
  };

  const handleSelectOne = (id: number) => {
    setSelectedOrders((prev) =>
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]
    );
  };

  const handleOpenCreate = () => {
    setEditingOrder({
      guestName: '',
      guestWhatsApp: '',
      guestEmail: '',
      guestAddress: '',
      deliveryMethod: 'retiro',
      paymentMethod: 'MercadoPago',
      status: 'pending',
      paymentStatus: 'pending',
      total: 24500,
    });
    setOrderItemsDraft([
      { productName: 'Lámpara Ripple 3D', quantity: 1, unitPrice: 24500, filamentColor: 'Blanco Hueso' },
    ]);
    setIsModalOpen(true);
  };

  const handleOpenEdit = (order: OrderData) => {
    setEditingOrder(order);
    setOrderItemsDraft(
      order.items && order.items.length > 0
        ? order.items
        : [{ productName: 'Producto Especial', quantity: 1, unitPrice: order.total }]
    );
    setIsModalOpen(true);
  };

  const handleSaveOrder = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingOrder?.guestName && !editingOrder?.customer?.fullName) {
      toast.error('El nombre del cliente es obligatorio');
      return;
    }

    try {
      const calculatedTotal = orderItemsDraft.reduce(
        (acc, item) => acc + item.quantity * item.unitPrice,
        0
      );

      const payload = {
        ...editingOrder,
        guestName: editingOrder.guestName || editingOrder.customer?.fullName,
        subtotal: calculatedTotal,
        total: calculatedTotal,
        items: orderItemsDraft.map((item) => ({
          productName: item.productName,
          quantity: Number(item.quantity) || 1,
          unitPrice: Number(item.unitPrice) || 0,
          filamentColor: item.filamentColor || 'Blanco Hueso',
          variantNotes: item.variantNotes || '',
        })),
      };

      if (editingOrder.id) {
        await api.put(`/orders/${editingOrder.id}`, payload);
        toast.success('Pedido actualizado con éxito');
      } else {
        await api.post('/orders', payload);
        toast.success('Pedido creado con éxito');
      }

      setIsModalOpen(false);
      loadOrders();
    } catch {
      toast.error('Error al guardar el pedido');
    }
  };

  const handleDelete = async () => {
    if (!deletingOrderId) return;
    try {
      await api.delete(`/orders/${deletingOrderId}`);
      setOrders((prev) => prev.filter((o) => o.id !== deletingOrderId));
      toast.success('Pedido eliminado');
      setDeletingOrderId(null);
    } catch {
      toast.error('No se pudo eliminar el pedido');
    }
  };

  // Filtrado
  const filteredOrders = orders.filter((order) => {
    if (selectedTab !== 'all' && order.status !== selectedTab) return false;
    if (searchTerm) {
      const query = searchTerm.toLowerCase();
      const customer = (order.customer?.fullName || order.guestName || '').toLowerCase();
      const orderId = order.id.toString();
      const product = (order.items?.[0]?.productName || '').toLowerCase();
      return customer.includes(query) || orderId.includes(query) || product.includes(query);
    }
    return true;
  });

  // Paginación
  const totalPages = Math.max(1, Math.ceil(filteredOrders.length / pageSize));
  const paginatedOrders = filteredOrders.slice(
    (currentPage - 1) * pageSize,
    currentPage * pageSize
  );

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'delivered':
        return (
          <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-[11px] font-semibold bg-emerald-500/10 text-emerald-600 border border-emerald-500/30">
            <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" /> Entregado
          </span>
        );
      case 'shipped':
        return (
          <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-[11px] font-semibold bg-blue-500/10 text-blue-600 border border-blue-500/30">
            <span className="h-1.5 w-1.5 rounded-full bg-blue-500" /> Enviado
          </span>
        );
      case 'pending':
        return (
          <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-[11px] font-semibold bg-amber-500/10 text-amber-600 border border-amber-500/30">
            <span className="h-1.5 w-1.5 rounded-full bg-amber-500" /> Pendiente
          </span>
        );
      case 'cancelled':
        return (
          <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-[11px] font-semibold bg-destructive/10 text-destructive border border-destructive/30">
            <span className="h-1.5 w-1.5 rounded-full bg-destructive" /> Cancelado
          </span>
        );
      default:
        return (
          <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-[11px] font-semibold bg-purple-500/10 text-purple-600 border border-purple-500/30">
            <span className="h-1.5 w-1.5 rounded-full bg-purple-500" /> En Taller
          </span>
        );
    }
  };

  return (
    <div className="space-y-6 max-w-7xl mx-auto pb-16 font-sans">
      {/* ── HEADER SUPERIOR: Title + Create Order Button (como en captura) ── */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold tracking-tight text-foreground">
            Pedidos
          </h1>
          <p className="text-xs text-muted-foreground mt-0.5">
            Gestión y seguimiento de órdenes, clientes y comprobantes de entrega
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            onClick={handleOpenCreate}
            size="sm"
            className="gap-1.5 text-xs font-semibold bg-foreground text-background hover:bg-foreground/90 shadow-2xs"
          >
            <Plus className="h-3.5 w-3.5" /> + Crear Pedido
          </Button>
        </div>
      </div>

      {/* ── BARRA DE TABS / FILTROS (Exacta a la captura: All, Pending, Shipped...) ── */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 border-b border-border/60 pb-3">
        {/* Tabs de Estado */}
        <div className="flex items-center gap-1 overflow-x-auto pb-1 sm:pb-0">
          {TABS.map((tab) => (
            <button
              key={tab.value}
              onClick={() => {
                setSelectedTab(tab.value);
                setCurrentPage(1);
              }}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors shrink-0 ${
                selectedTab === tab.value
                  ? 'bg-muted text-foreground font-semibold shadow-2xs'
                  : 'text-muted-foreground hover:text-foreground hover:bg-muted/40'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Acciones de la derecha: Buscador + Filtros + Columnas */}
        <div className="flex items-center gap-2">
          <div className="relative">
            <Search className="absolute left-2.5 top-2.5 h-3.5 w-3.5 text-muted-foreground" />
            <Input
              placeholder="Buscar por cliente o ID..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="h-8 pl-8 text-xs w-48 sm:w-56"
            />
          </div>
          <Button variant="outline" size="sm" className="h-8 px-2.5 text-xs gap-1.5">
            <Filter className="h-3 w-3 text-muted-foreground" /> Filtros
          </Button>
          <Button variant="outline" size="sm" className="h-8 px-2.5 text-xs gap-1.5">
            <SlidersHorizontal className="h-3 w-3 text-muted-foreground" /> Columnas
          </Button>
        </div>
      </div>

      {/* ── TABLA PRINCIPAL DE PEDIDOS (Estilo exacto a captura) ── */}
      <Card className="border-border/70 shadow-2xs overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="border-b border-border/60 text-muted-foreground text-left bg-muted/15 font-medium">
                <th className="py-3 px-3 w-8 text-center">
                  <input
                    type="checkbox"
                    checked={
                      filteredOrders.length > 0 &&
                      selectedOrders.length === filteredOrders.length
                    }
                    onChange={(e) => handleSelectAll(e.target.checked)}
                    className="rounded border-border text-primary focus:ring-primary h-3.5 w-3.5"
                  />
                </th>
                <th className="py-3 px-3 font-semibold text-foreground">Pedido</th>
                <th className="py-3 px-4 font-semibold text-foreground">Producto</th>
                <th className="py-3 px-4 font-semibold text-foreground">Cliente</th>
                <th className="py-3 px-3 font-semibold text-foreground">Tipo</th>
                <th className="py-3 px-4 font-semibold text-foreground">Precio</th>
                <th className="py-3 px-4 font-semibold text-foreground">Fecha</th>
                <th className="py-3 px-3 font-semibold text-foreground">Estado</th>
                <th className="py-3 px-3 w-10 text-center font-semibold text-foreground"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border/40">
              {isLoading ? (
                <tr>
                  <td colSpan={9} className="py-12 text-center text-muted-foreground text-xs">
                    Cargando pedidos...
                  </td>
                </tr>
              ) : paginatedOrders.length === 0 ? (
                <tr>
                  <td colSpan={9} className="py-12 text-center text-muted-foreground text-xs">
                    No se encontraron pedidos con este filtro.
                  </td>
                </tr>
              ) : (
                paginatedOrders.map((order) => {
                  const firstItem = order.items?.[0];
                  const itemImg = firstItem?.product?.mainImageUrl
                    ? resolveImageUrl(firstItem.product.mainImageUrl)
                    : null;
                  const customerName =
                    order.customer?.fullName || order.guestName || 'Cliente Particular';
                  const customerEmail =
                    order.customer?.email || order.guestEmail || order.guestWhatsApp || 'Sin email';
                  const isChecked = selectedOrders.includes(order.id);
                  const orderDate = new Date(order.createdAt).toLocaleDateString('es-AR', {
                    day: 'numeric',
                    month: 'short',
                    year: 'numeric',
                  });

                  return (
                    <tr
                      key={order.id}
                      className={`hover:bg-muted/20 transition-colors ${
                        isChecked ? 'bg-muted/30' : ''
                      }`}
                    >
                      {/* Checkbox */}
                      <td className="py-3 px-3 text-center">
                        <input
                          type="checkbox"
                          checked={isChecked}
                          onChange={() => handleSelectOne(order.id)}
                          className="rounded border-border text-primary focus:ring-primary h-3.5 w-3.5"
                        />
                      </td>

                      {/* Order ID */}
                      <td className="py-3 px-3 font-medium text-foreground">
                        <Link
                          to={`/orders/${order.id}`}
                          className="hover:text-primary transition-colors hover:underline"
                        >
                          #{order.id.toString().padStart(5, '0')}
                        </Link>
                      </td>

                      {/* Product Thumbnail + Name */}
                      <td className="py-3 px-4">
                        <div className="flex items-center gap-2.5">
                          <div className="h-8 w-8 rounded-md border border-border bg-muted/40 overflow-hidden shrink-0 flex items-center justify-center">
                            {itemImg ? (
                              <img
                                src={itemImg}
                                alt={firstItem?.productName}
                                className="w-full h-full object-cover"
                              />
                            ) : (
                              <ImageIcon className="h-4 w-4 text-muted-foreground/40" />
                            )}
                          </div>
                          <span className="font-medium text-foreground truncate max-w-[170px]">
                            {firstItem?.productName || 'Producto Mila 3D'}
                            {order.items && order.items.length > 1 && (
                              <span className="text-[10px] text-muted-foreground ml-1">
                                (+{order.items.length - 1})
                              </span>
                            )}
                          </span>
                        </div>
                      </td>

                      {/* Customer */}
                      <td className="py-3 px-4">
                        <div className="leading-tight">
                          <div className="font-semibold text-foreground">{customerName}</div>
                          <div className="text-[11px] text-muted-foreground truncate max-w-[160px]">
                            {customerEmail}
                          </div>
                        </div>
                      </td>

                      {/* Type */}
                      <td className="py-3 px-3 text-muted-foreground font-medium">
                        {order.source === 'taller' ? 'Taller' : 'Venta'}
                      </td>

                      {/* Price */}
                      <td className="py-3 px-4 font-semibold text-foreground font-mono">
                        ${order.total.toLocaleString('es-AR')}
                      </td>

                      {/* Date */}
                      <td className="py-3 px-4 text-muted-foreground whitespace-nowrap">
                        {orderDate}
                      </td>

                      {/* Status */}
                      <td className="py-3 px-3 whitespace-nowrap">
                        {getStatusBadge(order.status)}
                      </td>

                      {/* Actions Menu (...) */}
                      <td className="py-3 px-3 text-center">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-7 w-7 text-muted-foreground hover:text-foreground"
                            >
                              <MoreHorizontal className="h-3.5 w-3.5" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end" className="text-xs">
                            <DropdownMenuItem asChild>
                              <Link to={`/orders/${order.id}`} className="gap-2 cursor-pointer">
                                <Eye className="h-3.5 w-3.5" /> Ver Detalle
                              </Link>
                            </DropdownMenuItem>
                            <DropdownMenuItem asChild>
                              <Link to={`/orders/${order.id}`} className="gap-2 cursor-pointer">
                                <Printer className="h-3.5 w-3.5" /> Imprimir Comprobante
                              </Link>
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              onClick={() => handleOpenEdit(order)}
                              className="gap-2 cursor-pointer"
                            >
                              <Edit2 className="h-3.5 w-3.5" /> Editar Pedido
                            </DropdownMenuItem>
                            {(order.guestWhatsApp || order.customer?.phoneNumber) && (
                              <DropdownMenuItem asChild>
                                <a
                                  href={`https://wa.me/${formatWhatsAppNumber(order.guestWhatsApp || order.customer?.phoneNumber || '')}?text=${encodeURIComponent(`¡Hola ${customerName}! Nos comunicamos por tu pedido #${order.id} en Mila Design Studio.`)}`}
                                  target="_blank"
                                  rel="noreferrer"
                                  className="gap-2 cursor-pointer text-emerald-600"
                                >
                                  <MessageCircle className="h-3.5 w-3.5" /> WhatsApp
                                </a>
                              </DropdownMenuItem>
                            )}
                            <DropdownMenuSeparator />
                            <DropdownMenuItem
                              onClick={() => setDeletingOrderId(order.id)}
                              className="gap-2 cursor-pointer text-destructive focus:text-destructive"
                            >
                              <Trash2 className="h-3.5 w-3.5" /> Eliminar
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* ── PAGINACIÓN INFERIOR (Exacta a la captura: Rows per page, 1 - 10 of 15 < 1 2 >) ── */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 px-4 py-3 border-t border-border/60 text-xs text-muted-foreground">
          <div className="flex items-center gap-2">
            <span>Filas por página:</span>
            <select
              value={pageSize}
              onChange={(e) => {
                setPageSize(Number(e.target.value));
                setCurrentPage(1);
              }}
              className="h-7 px-2 rounded-md border border-border bg-background text-xs font-medium"
            >
              <option value={10}>10</option>
              <option value={20}>20</option>
              <option value={50}>50</option>
            </select>
          </div>

          <div className="flex items-center gap-3">
            <span>
              {filteredOrders.length === 0
                ? '0 de 0'
                : `${(currentPage - 1) * pageSize + 1} - ${Math.min(
                    currentPage * pageSize,
                    filteredOrders.length
                  )} de ${filteredOrders.length}`}
            </span>
            <div className="flex items-center gap-1">
              <Button
                variant="outline"
                size="icon"
                disabled={currentPage === 1}
                onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                className="h-7 w-7"
              >
                <ChevronLeft className="h-3.5 w-3.5" />
              </Button>
              {Array.from({ length: totalPages }).map((_, i) => (
                <Button
                  key={i}
                  variant={currentPage === i + 1 ? 'default' : 'outline'}
                  size="icon"
                  onClick={() => setCurrentPage(i + 1)}
                  className="h-7 w-7 text-xs"
                >
                  {i + 1}
                </Button>
              ))}
              <Button
                variant="outline"
                size="icon"
                disabled={currentPage === totalPages}
                onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                className="h-7 w-7"
              >
                <ChevronRight className="h-3.5 w-3.5" />
              </Button>
            </div>
          </div>
        </div>
      </Card>

      {/* ── MODAL: CREAR / EDITAR PEDIDO ── */}
      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <DialogContent className="sm:max-w-xl max-h-[90vh] overflow-y-auto">
          <form onSubmit={handleSaveOrder}>
            <DialogHeader>
              <DialogTitle className="text-base font-bold">
                {editingOrder?.id ? `Editar Pedido #${editingOrder.id}` : 'Crear Nuevo Pedido'}
              </DialogTitle>
            </DialogHeader>

            <div className="space-y-4 py-3 text-xs">
              {/* Cliente */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label className="text-xs">Nombre del Cliente *</Label>
                  <Input
                    placeholder="ej. Lucía Fernández"
                    value={editingOrder?.guestName || editingOrder?.customer?.fullName || ''}
                    onChange={(e) =>
                      setEditingOrder((prev) => ({ ...prev, guestName: e.target.value }))
                    }
                    className="h-8 text-xs"
                    required
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">WhatsApp / Teléfono</Label>
                  <Input
                    placeholder="ej. 1159265776"
                    value={editingOrder?.guestWhatsApp || editingOrder?.customer?.phoneNumber || ''}
                    onChange={(e) =>
                      setEditingOrder((prev) => ({ ...prev, guestWhatsApp: e.target.value }))
                    }
                    className="h-8 text-xs font-mono"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label className="text-xs">Email</Label>
                  <Input
                    placeholder="lucia@gmail.com"
                    value={editingOrder?.guestEmail || editingOrder?.customer?.email || ''}
                    onChange={(e) =>
                      setEditingOrder((prev) => ({ ...prev, guestEmail: e.target.value }))
                    }
                    className="h-8 text-xs"
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Dirección de Entrega</Label>
                  <Input
                    placeholder="Av. Santa Fe 1234, CABA"
                    value={editingOrder?.guestAddress || ''}
                    onChange={(e) =>
                      setEditingOrder((prev) => ({ ...prev, guestAddress: e.target.value }))
                    }
                    className="h-8 text-xs"
                  />
                </div>
              </div>

              {/* Items del Pedido */}
              <div className="pt-2 border-t border-border/50">
                <div className="flex items-center justify-between mb-2">
                  <Label className="text-xs font-bold">Ítems del Pedido</Label>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="h-7 text-[11px] gap-1"
                    onClick={() =>
                      setOrderItemsDraft((prev) => [
                        ...prev,
                        { productName: '', quantity: 1, unitPrice: 0, filamentColor: 'Blanco Hueso' },
                      ])
                    }
                  >
                    <Plus className="h-3 w-3" /> Añadir Ítem
                  </Button>
                </div>

                <div className="space-y-2">
                  {orderItemsDraft.map((item, idx) => (
                    <div
                      key={idx}
                      className="p-2.5 rounded-lg border border-border bg-muted/20 grid grid-cols-12 gap-2 items-center"
                    >
                      <div className="col-span-5">
                        <Input
                          placeholder="Nombre producto"
                          value={item.productName}
                          onChange={(e) =>
                            setOrderItemsDraft((prev) =>
                              prev.map((it, i) =>
                                i === idx ? { ...it, productName: e.target.value } : it
                              )
                            )
                          }
                          className="h-7 text-xs"
                          required
                        />
                      </div>
                      <div className="col-span-2">
                        <Input
                          type="number"
                          placeholder="Cant."
                          value={item.quantity}
                          onChange={(e) =>
                            setOrderItemsDraft((prev) =>
                              prev.map((it, i) =>
                                i === idx ? { ...it, quantity: Number(e.target.value) } : it
                              )
                            )
                          }
                          className="h-7 text-xs text-center"
                          min={1}
                        />
                      </div>
                      <div className="col-span-3">
                        <Input
                          type="number"
                          placeholder="Precio ($)"
                          value={item.unitPrice}
                          onChange={(e) =>
                            setOrderItemsDraft((prev) =>
                              prev.map((it, i) =>
                                i === idx ? { ...it, unitPrice: Number(e.target.value) } : it
                              )
                            )
                          }
                          className="h-7 text-xs"
                        />
                      </div>
                      <div className="col-span-2 text-right">
                        {orderItemsDraft.length > 1 && (
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            className="h-7 w-7 text-destructive"
                            onClick={() =>
                              setOrderItemsDraft((prev) => prev.filter((_, i) => i !== idx))
                            }
                          >
                            <Trash2 className="h-3 w-3" />
                          </Button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Estados y Pago */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 pt-2 border-t border-border/50">
                <div className="space-y-1">
                  <Label className="text-xs">Estado Pedido</Label>
                  <select
                    value={editingOrder?.status || 'pending'}
                    onChange={(e) =>
                      setEditingOrder((prev) => ({ ...prev, status: e.target.value }))
                    }
                    className="w-full h-8 px-2 rounded-md border border-border bg-background text-xs"
                  >
                    <option value="pending">Pendiente</option>
                    <option value="in_production">En Taller</option>
                    <option value="shipped">Enviado</option>
                    <option value="delivered">Entregado</option>
                    <option value="cancelled">Cancelado</option>
                  </select>
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Estado Pago</Label>
                  <select
                    value={editingOrder?.paymentStatus || 'pending'}
                    onChange={(e) =>
                      setEditingOrder((prev) => ({ ...prev, paymentStatus: e.target.value }))
                    }
                    className="w-full h-8 px-2 rounded-md border border-border bg-background text-xs"
                  >
                    <option value="approved">Aprobado</option>
                    <option value="pending">Pendiente</option>
                  </select>
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Método Pago</Label>
                  <Input
                    placeholder="MercadoPago / Efectivo"
                    value={editingOrder?.paymentMethod || 'MercadoPago'}
                    onChange={(e) =>
                      setEditingOrder((prev) => ({ ...prev, paymentMethod: e.target.value }))
                    }
                    className="h-8 text-xs"
                  />
                </div>
              </div>
            </div>

            <DialogFooter className="pt-2">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setIsModalOpen(false)}
              >
                Cancelar
              </Button>
              <Button type="submit" size="sm">
                Guardar Pedido
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* ── MODAL: CONFIRMACIÓN DE BORRADO ── */}
      <Dialog open={deletingOrderId !== null} onOpenChange={() => setDeletingOrderId(null)}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle className="text-base font-bold">¿Eliminar Pedido?</DialogTitle>
          </DialogHeader>
          <p className="text-xs text-muted-foreground py-2">
            El pedido será marcado como inactivo. Podrás restaurarlo si es necesario.
          </p>
          <DialogFooter>
            <Button variant="outline" size="sm" onClick={() => setDeletingOrderId(null)}>
              Cancelar
            </Button>
            <Button variant="destructive" size="sm" onClick={handleDelete}>
              Eliminar Pedido
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
